import React, { useState, useEffect, useCallback } from 'react'
import { RefreshCcw, Search, Filter, Clock, CheckCircle, XCircle, AlertTriangle, FileText, Shield, User, Eye, ScanLine, Play, Trash2 } from 'lucide-react'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { useAuth } from '../../contexts/AuthContext'

const STATUS_CONFIG = {
  OCR_CAPTURE: { label: 'OCR only (pre-DVS)', color: 'teal', icon: ScanLine },
  DRAFT: { label: 'Draft', color: 'gray', icon: FileText },
  DOCUMENTS_PENDING: { label: 'Documents Pending', color: 'yellow', icon: FileText },
  DVS_PENDING: { label: 'DVS Pending', color: 'blue', icon: Shield },
  DVS_PROCESSING: { label: 'DVS Processing', color: 'blue', icon: Shield },
  DVS_FAILED: { label: 'DVS Failed', color: 'red', icon: XCircle },
  AML_PENDING: { label: 'AML Pending', color: 'purple', icon: Shield },
  AML_PROCESSING: { label: 'AML Processing', color: 'purple', icon: Shield },
  MANUAL_REVIEW: { label: 'Manual Review Required', color: 'orange', icon: AlertTriangle },
  COMPLETED: { label: 'Completed', color: 'green', icon: CheckCircle },
  REJECTED: { label: 'Rejected', color: 'red', icon: XCircle }
}

const FILTER_OPTIONS = [
  { value: 'ALL', label: 'All Statuses' },
  { value: 'PENDING', label: 'All Pending' },
  { value: 'DVS_PENDING', label: 'DVS Pending' },
  { value: 'DVS_FAILED', label: 'DVS Failed' },
  { value: 'AML_PENDING', label: 'AML Pending' },
  { value: 'MANUAL_REVIEW', label: 'Manual Review' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'REJECTED', label: 'Rejected' }
]

const EMPTY_STATS = {
  total: 0,
  dvsPending: 0,
  dvsFailed: 0,
  amlPending: 0,
  amlFailed: 0,
  manualReview: 0,
  completed: 0,
  ocrCaptures: 0
}

/** ProfileSafe profileID from API, ocr_data, or OCR row (used for GreenID ref). */
function resolveProfileSafeId(item) {
  if (!item) return null
  const direct =
    item.profilesafeProfileId ||
    item.ocrData?.profilesafe_profile_id ||
    item.ocrData?.profile_id ||
    item.ocrData?.profileID ||
    item.patronSnapshot?.profileID ||
    item.patronSnapshot?.profilesafe_profile_id ||
    null
  if (direct && String(direct).trim()) return String(direct).trim()
  if (item.rowKind === 'OCR' && item.masterProfileId && !String(item.masterProfileId).startsWith('ocr_')) {
    return item.masterProfileId
  }
  return null
}

function formatProfileSafeIdShort(id) {
  if (!id) return '—'
  const s = String(id)
  if (s.length <= 16) return s
  return `${s.slice(0, 8)}…${s.slice(-6)}`
}

function dvsResponseSummary(item) {
  if (!item) return '—'
  if (item.errorMessage) return String(item.errorMessage)
  const r = item.dvsResult
  if (!r) {
    if (item.dvsCheckedAt) return 'No stored response payload'
    return 'Not run yet'
  }
  if (r.verified === true) return 'Verified'
  if (r.failure_reason) return String(r.failure_reason)
  if (r.message) return String(r.message)
  if (r.error) return String(r.error)
  return r.verified === false ? 'Not verified' : 'See full response'
}

function DvsResponsePanel({ item }) {
  const [showRaw, setShowRaw] = React.useState(true)
  if (!item || item.rowKind === 'OCR') return null
  const summary = dvsResponseSummary(item)
  const hasPayload = item.dvsResult || item.errorMessage || item.ocrData

  return (
    <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-medium text-gray-900">DVS response</div>
          <p className="text-gray-700 mt-1 break-words">{summary}</p>
        </div>
        {item.dvsCheckedAt && (
          <span className="text-xs text-gray-500 whitespace-nowrap">
            {new Date(item.dvsCheckedAt).toLocaleString()}
          </span>
        )}
      </div>
      {item.retryCount > 0 && (
        <p className="text-xs text-gray-500 mt-1">Attempts recorded: {item.retryCount}</p>
      )}
      {hasPayload && (
        <>
          <button
            type="button"
            className="text-xs text-psg-teal mt-2 underline"
            onClick={() => setShowRaw((v) => !v)}
          >
            {showRaw ? 'Hide' : 'Show'} full JSON
          </button>
          {showRaw && (
            <div className="mt-2 space-y-2">
              {(item.ocrData || item.rowKind === 'PIPELINE') && (
                <div>
                  <div className="text-xs font-medium text-gray-600 mb-1">DVS document data (stored + row)</div>
                  <pre className="text-xs bg-white border rounded p-2 overflow-auto max-h-40 whitespace-pre-wrap">
                    {JSON.stringify(
                      item.rowKind === 'PIPELINE' ? effectiveDvsFields(item) : item.ocrData,
                      null,
                      2
                    )}
                  </pre>
                </div>
              )}
              {item.errorMessage && (
                <div>
                  <div className="text-xs font-medium text-red-700 mb-1">Error</div>
                  <pre className="text-xs bg-white border border-red-100 rounded p-2 overflow-auto max-h-32 whitespace-pre-wrap text-red-900">
                    {item.errorMessage}
                  </pre>
                </div>
              )}
              {item.dvsResult && (
                <div>
                  <div className="text-xs font-medium text-gray-600 mb-1">Provider result</div>
                  <pre className="text-xs bg-white border rounded p-2 overflow-auto max-h-64 whitespace-pre-wrap">
                    {JSON.stringify(item.dvsResult, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

function needsLicenceCardNumber(item) {
  if (!item?.ocrData) return false
  const dt = String(item.ocrData.document_type || '').toLowerCase().replace(/-/g, '_')
  const cc = String(item.ocrData.country_code || 'AU').trim().toUpperCase()
  return (dt === 'drivers_licence' || dt === 'proof_of_age') && (cc === 'AU' || cc === 'AUS')
}

function ocrLicenceCardNumber(ocr) {
  if (!ocr) return ''
  return String(
    ocr.document_card_number || ocr.licence_card_number || ocr.rta_number || ''
  ).trim()
}

function formatDobDisplay(val) {
  if (!val) return '—'
  const s = String(val)
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`
  return s.slice(0, 32)
}

/** Merged view: ocr_data JSON + queue row name/DOB columns. */
function effectiveDvsFields(item) {
  if (!item) return {}
  const ocr = item.ocrData || {}
  return {
    document_type: ocr.document_type || (item.payoutId || item.documentUrls ? 'drivers_licence' : ''),
    document_number: ocr.document_number || '',
    document_card_number: ocrLicenceCardNumber(ocr),
    given_name: ocr.given_name || item.firstName || '',
    family_name: ocr.family_name || item.lastName || '',
    date_of_birth: ocr.date_of_birth || (item.dateOfBirth ? String(item.dateOfBirth).slice(0, 10) : ''),
    state_of_issue: ocr.state_of_issue || 'NSW',
    country_code: ocr.country_code || 'AU',
    ocr_source: ocr.ocr_source || null,
  }
}

function isPayoutPipelineRow(item) {
  return !!(item?.payoutId && item.rowKind !== 'OCR')
}

function formatDvsProviderForUi(provider) {
  if (provider == null || provider === '') return '—'
  const u = String(provider).toUpperCase()
  if (u === 'GREENID' || u === 'GREEN_ID') return 'DVS'
  return String(provider)
}

const DOC_SLOTS = [
  { key: 'id_front', label: 'ID front' },
  { key: 'id_back', label: 'ID back' },
  { key: 'patron_photo', label: 'Patron photo' },
]

function QueueDocumentImage({ queueId, slot, label }) {
  const [src, setSrc] = React.useState(null)
  const [failed, setFailed] = React.useState(false)

  React.useEffect(() => {
    let revoked = null
    setSrc(null)
    setFailed(false)
    const url = getLocalApiUrl(
      `/api/profiles/verification-queue/${encodeURIComponent(queueId)}/document/${slot}`
    )
    authFetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.blob()
      })
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob)
        revoked = objectUrl
        setSrc(objectUrl)
      })
      .catch(() => setFailed(true))
    return () => {
      if (revoked) URL.revokeObjectURL(revoked)
    }
  }, [queueId, slot])

  return (
    <div className="border rounded-lg overflow-hidden bg-gray-50">
      <div className="text-xs font-medium text-gray-600 px-2 py-1 border-b bg-white">{label}</div>
      <div className="min-h-[120px] flex items-center justify-center p-2">
        {src ? (
          <img src={src} alt={label} className="max-h-48 w-full object-contain rounded" />
        ) : failed ? (
          <span className="text-xs text-gray-500">Not available</span>
        ) : (
          <span className="text-xs text-gray-400">Loading…</span>
        )}
      </div>
    </div>
  )
}

function QueueDocumentGallery({ profile }) {
  const queueId = profile?.id
  const hasRefs =
    profile?.documentUrls &&
    typeof profile.documentUrls === 'object' &&
    DOC_SLOTS.some((s) => profile.documentUrls[s.key])
  const docCount = Number(profile?.documentsCount) || 0

  if (!queueId) return null
  if (!hasRefs) {
    const dvsDone = String(profile?.dvsStatus || '').toUpperCase() === 'VERIFIED'
    return (
      <p className="text-sm text-gray-500 mt-2">
        {dvsDone
          ? 'Licence images are hidden after DVS verification per retention policy. DVS document fields remain below.'
          : docCount > 0
            ? `Document metadata recorded (${docCount} on file) but ID images are not stored for this item. Web onboarding and DVS use OCR fields only; images appear when linked to a payout with front/back scans.`
            : 'No ID images on file for this queue item. Images are stored when a payout includes ID front/back scans (mobile or monitoring capture).'}
      </p>
    )
  }

  return (
    <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
      {DOC_SLOTS.filter(({ key }) => profile.documentUrls[key]).map(({ key, label }) => (
        <QueueDocumentImage key={key} queueId={queueId} slot={key} label={label} />
      ))}
    </div>
  )
}

export default function VerificationQueue() {
  const { can } = useAuth()
  const canManage = can('profile-safe/checks', 'manage')

  const [items, setItems] = useState([])
  const [ocrItems, setOcrItems] = useState([])
  const [ocrListIncluded, setOcrListIncluded] = useState(true)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedProfile, setSelectedProfile] = useState(null)
  const [processDvsLoading, setProcessDvsLoading] = useState(false)
  const [processDvsMessage, setProcessDvsMessage] = useState(null)
  const [clearLoading, setClearLoading] = useState(null)
  const [clearMessage, setClearMessage] = useState(null)
  const [retryingId, setRetryingId] = useState(null)
  const [runningAmlId, setRunningAmlId] = useState(null)
  const [dvsCardNumber, setDvsCardNumber] = useState('')
  const [dvsFields, setDvsFields] = useState({
    document_type: 'drivers_licence',
    document_number: '',
    document_card_number: '',
    given_name: '',
    family_name: '',
    date_of_birth: '',
    state_of_issue: 'NSW',
  })
  const [rowActionMessage, setRowActionMessage] = useState(null)
  const [filters, setFilters] = useState({
    status: 'ALL',
    search: '',
    profileType: 'ALL'
  })
  const [stats, setStats] = useState({ ...EMPTY_STATS })
  const [listTab, setListTab] = useState('dvs')

  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const params = new URLSearchParams()
      if (filters.status !== 'ALL') {
        if (filters.status === 'PENDING') {
          params.append('status', 'DVS_PENDING,DVS_PROCESSING,AML_PENDING,AML_PROCESSING')
        } else {
          params.append('status', filters.status)
        }
      }
      if (filters.profileType !== 'ALL') {
        params.append('profileType', filters.profileType)
      }
      if (filters.search) {
        params.append('search', filters.search)
      }

      try {
        const response = await authFetch(getLocalApiUrl(`/api/profiles/verification-queue?${params}`))
        const result = await response.json().catch(() => ({}))

        if (response.ok && result.success) {
          setItems(result.data || [])
          setOcrItems(result.ocrSubmissions || [])
          setOcrListIncluded(result.ocrListIncluded !== false)
          setStats({ ...EMPTY_STATS, ...(result.stats || {}) })
          return
        }

        const msg =
          result.error ||
          result.message ||
          (response.status === 403
            ? 'You do not have permission to view the verification queue (requires profile-safe/checks: view).'
            : response.status
              ? `Could not load verification queue (HTTP ${response.status}).`
              : 'Could not load verification queue.')
        setError(msg)
        setItems([])
        setOcrItems([])
        setOcrListIncluded(false)
        setStats({ ...EMPTY_STATS })
      } catch (apiError) {
        console.log('API not available:', apiError.message)
        setError(apiError.message || 'Could not reach the API.')
        setItems([])
        setOcrItems([])
        setOcrListIncluded(false)
        setStats({ ...EMPTY_STATS })
      }

    } catch (err) {
      console.error('Load error:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadData()
    }, 30000)
    return () => clearInterval(interval)
  }, [loadData])

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.DRAFT
    const Icon = config.icon
    
    const colorClasses = {
      gray: 'bg-gray-100 text-gray-800',
      yellow: 'bg-yellow-100 text-yellow-800',
      blue: 'bg-blue-100 text-blue-800',
      purple: 'bg-purple-100 text-purple-800',
      orange: 'bg-orange-100 text-orange-800',
      green: 'bg-green-100 text-green-800',
      red: 'bg-red-100 text-red-800',
      teal: 'bg-teal-100 text-teal-800'
    }

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${colorClasses[config.color]}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    )
  }

  const getProfileTypeBadge = (type) => {
    const badges = {
      MEMBER: { label: 'New Member', color: 'bg-blue-100 text-blue-800' },
      EXISTING_MEMBER: { label: 'Existing Member', color: 'bg-green-100 text-green-800' },
      VISITOR: { label: 'Visitor', color: 'bg-yellow-100 text-yellow-800' }
    }
    const badge = badges[type] || badges.MEMBER
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${badge.color}`}>
        {badge.label}
      </span>
    )
  }

  const getTimeSince = (timestamp) => {
    const seconds = Math.floor((Date.now() - new Date(timestamp)) / 1000)
    if (seconds < 60) return `${seconds}s ago`
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.floor(hours / 24)
    return `${days}d ago`
  }

  const syncDvsFieldsFromItem = (item) => {
    const ef = effectiveDvsFields(item)
    setDvsCardNumber(ef.document_card_number || '')
    setDvsFields({
      document_type: ef.document_type || 'drivers_licence',
      document_number: ef.document_number || '',
      document_card_number: ef.document_card_number || '',
      given_name: ef.given_name || '',
      family_name: ef.family_name || '',
      date_of_birth: ef.date_of_birth || '',
      state_of_issue: ef.state_of_issue || 'NSW',
    })
  }

  const handleViewProfile = (item) => {
    setSelectedProfile(item)
    syncDvsFieldsFromItem(item)
  }

  useEffect(() => {
    if (selectedProfile?.id) syncDvsFieldsFromItem(selectedProfile)
  }, [selectedProfile?.id, selectedProfile?.ocrData, selectedProfile?.updatedAt])

  const mergeQueueItem = (updated) => {
    if (!updated?.id) return
    setItems((prev) => prev.map((i) => (i.id === updated.id ? { ...i, ...updated } : i)))
    setSelectedProfile((prev) => (prev?.id === updated.id ? { ...prev, ...updated } : prev))
  }

  const canRunAmlForItem = (item) => {
    if (!item || item.rowKind === 'OCR') return false
    const dvs = String(item.dvsStatus || '').toUpperCase()
    const aml = String(item.amlStatus || 'PENDING').toUpperCase()
    return dvs === 'VERIFIED' && !['CLEAR', 'MATCH'].includes(aml)
  }

  const runAmlForItem = async (item) => {
    if (!canManage || !item?.id || item.rowKind === 'OCR') return
    try {
      setRunningAmlId(item.id)
      setRowActionMessage(null)
      const response = await authFetch(
        getLocalApiUrl(`/api/profiles/verification-queue/${encodeURIComponent(item.id)}/run-aml`),
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }
      )
      const result = await response.json().catch(() => ({}))
      if (result.data) mergeQueueItem(result.data)
      if (!response.ok || result.success === false) {
        throw new Error(result.error || result.message || `AML run failed (HTTP ${response.status})`)
      }
      setRowActionMessage({
        id: item.id,
        type: 'ok',
        text: result.message || 'AML/PEP screening completed (GreenID watchlist)',
      })
    } catch (e) {
      setRowActionMessage({
        id: item.id,
        type: 'err',
        text: e.message || 'AML run failed',
      })
    } finally {
      setRunningAmlId(null)
    }
  }

  const runDvsForItem = async (item) => {
    if (!canManage || !item?.id || item.rowKind === 'OCR') return
    const ef = {
      ...dvsFields,
      document_card_number: dvsFields.document_card_number || dvsCardNumber,
      country_code: 'AU',
    }
    const card = String(ef.document_card_number || '').trim()
    if (!ef.document_number?.trim() || !ef.given_name?.trim() || !ef.family_name?.trim()) {
      setRowActionMessage({
        id: item.id,
        type: 'err',
        text: 'Fill licence number (front), given name, and family name in DVS document data (detail panel), then Run DVS.',
      })
      if (!selectedProfile || selectedProfile.id !== item.id) handleViewProfile(item)
      return
    }
    if (needsLicenceCardNumber({ ...item, ocrData: ef }) && !card) {
      setRowActionMessage({
        id: item.id,
        type: 'err',
        text:
          'Enter card number (required by GBG for AU DVS — separate from licence number), then Run DVS. GBG test: card 1111111111, licence 11111111.',
      })
      if (!selectedProfile || selectedProfile.id !== item.id) handleViewProfile(item)
      return
    }
    try {
      setRetryingId(item.id)
      setRowActionMessage(null)
      const retryBody = {
        document_type: ef.document_type,
        document_number: ef.document_number,
        document_card_number: card || undefined,
        given_name: ef.given_name,
        family_name: ef.family_name,
        date_of_birth: ef.date_of_birth || undefined,
        state_of_issue: ef.state_of_issue,
        country_code: ef.country_code || 'AU',
      }
      const response = await authFetch(
        getLocalApiUrl(`/api/profiles/verification-queue/${encodeURIComponent(item.id)}/retry-dvs`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(retryBody),
        }
      )
      const result = await response.json().catch(() => ({}))
      if (result.data) mergeQueueItem(result.data)
      if (!response.ok || result.success === false) {
        throw new Error(result.error || result.message || `DVS run failed (HTTP ${response.status})`)
      }
      setRowActionMessage({
        id: item.id,
        type: 'ok',
        text: result.message || 'DVS run completed',
      })
    } catch (e) {
      setRowActionMessage({
        id: item.id,
        type: 'err',
        text: e.message || 'DVS run failed',
      })
    } finally {
      setRetryingId(null)
    }
  }

  const confirmClearPhrase = (label) => {
    if (!window.confirm(`Delete ALL ${label} for this tenant? This cannot be undone.`)) return null
    const typed = window.prompt('Type CLEAR to confirm:')
    if (typed !== 'CLEAR') {
      setClearMessage('Clear cancelled — you must type CLEAR exactly.')
      return null
    }
    return 'CLEAR'
  }

  const clearQueue = async (target) => {
    const isPipeline = target === 'pipeline'
    const label = isPipeline ? 'DVS/AML pipeline rows' : 'ProfileSafe OCR captures'
    const phrase = confirmClearPhrase(label)
    if (!phrase) return
    const path = isPipeline ? 'clear-pipeline' : 'clear-ocr'
    try {
      setClearLoading(target)
      setClearMessage(null)
      const response = await authFetch(getLocalApiUrl(`/api/profiles/verification-queue/${path}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirmPhrase: phrase }),
      })
      const result = await response.json().catch(() => ({}))
      if (!response.ok || !result.success) {
        throw new Error(result.error || result.message || `Clear failed (HTTP ${response.status})`)
      }
      setClearMessage(result.message || `Cleared ${label}.`)
      setSelectedProfile(null)
      await loadData()
    } catch (e) {
      setClearMessage(e.message || 'Clear failed')
    } finally {
      setClearLoading(null)
    }
  }

  const runProcessDvsQueue = async () => {
    try {
      setProcessDvsLoading(true)
      setProcessDvsMessage(null)
      const response = await authFetch(getLocalApiUrl('/api/profiles/verification-queue/process-dvs'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}'
      })
      const result = await response.json().catch(() => ({}))
      if (!response.ok || !result.success) {
        throw new Error(result.error || result.message || 'Process DVS failed')
      }
      const processed = result.processed ?? 0
      setProcessDvsMessage(
        processed === 0
          ? 'No rows in DVS Pending status. The batch button only processes pending items — use Run DVS on each row to retry failed or re-test individual records.'
          : `Processed ${processed} item(s): ${result.succeeded ?? 0} ok, ${result.failed ?? 0} failed.`
      )
      await loadData()
    } catch (e) {
      setProcessDvsMessage(e.message || 'Could not run DVS processor (check permissions).')
    } finally {
      setProcessDvsLoading(false)
    }
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Verification Queue</h1>
            <p className="text-sm text-gray-600 mt-1">
              Monitor profile onboarding and verification status in real-time
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadData}
              disabled={loading}
              className="btn-secondary"
            >
              <RefreshCcw className={`w-4 h-4 inline mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            {canManage && (
            <button
              type="button"
              onClick={runProcessDvsQueue}
              disabled={processDvsLoading}
              className="btn-primary"
              title="Processes up to 10 rows in DVS Pending status only"
            >
              {processDvsLoading ? (
                <RefreshCcw className="w-4 h-4 inline mr-2 animate-spin" />
              ) : (
                <Shield className="w-4 h-4 inline mr-2" />
              )}
              Process DVS queue
            </button>
            )}
            {canManage && (
              <>
                <button
                  type="button"
                  onClick={() => clearQueue('pipeline')}
                  disabled={!!clearLoading}
                  className="btn-secondary text-red-700 border-red-200 hover:bg-red-50"
                  title="Remove all DVS/AML pipeline rows (verification_queue)"
                >
                  {clearLoading === 'pipeline' ? (
                    <RefreshCcw className="w-4 h-4 inline mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 inline mr-2" />
                  )}
                  Clear pipeline
                </button>
                <button
                  type="button"
                  onClick={() => clearQueue('ocr')}
                  disabled={!!clearLoading}
                  className="btn-secondary text-red-700 border-red-200 hover:bg-red-50"
                  title="Remove all ProfileSafe OCR-only captures"
                >
                  {clearLoading === 'ocr' ? (
                    <RefreshCcw className="w-4 h-4 inline mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 inline mr-2" />
                  )}
                  Clear OCR queue
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {clearMessage && (
        <div className="card text-sm text-gray-700 bg-amber-50 border border-amber-200">{clearMessage}</div>
      )}

      {processDvsMessage && (
        <div className="card text-sm text-gray-700 bg-slate-50 border border-slate-200">
          {processDvsMessage}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-4">
        <div className="card">
          <div className="text-sm text-gray-600">Pipeline total</div>
          <div className="text-3xl font-bold mt-2">{stats.total}</div>
        </div>
        <div className="card border-l-4 border-teal-500">
          <div className="text-sm text-gray-600">OCR captures</div>
          <div className="text-3xl font-bold mt-2 text-teal-600">{stats.ocrCaptures ?? 0}</div>
        </div>
        <div className="card border-l-4 border-blue-500">
          <div className="text-sm text-gray-600">DVS Pending</div>
          <div className="text-3xl font-bold mt-2 text-blue-600">{stats.dvsPending}</div>
        </div>
        <div className="card border-l-4 border-red-500">
          <div className="text-sm text-gray-600">DVS Failed</div>
          <div className="text-3xl font-bold mt-2 text-red-600">{stats.dvsFailed ?? 0}</div>
        </div>
        <div className="card border-l-4 border-purple-500">
          <div className="text-sm text-gray-600">AML Pending</div>
          <div className="text-3xl font-bold mt-2 text-purple-600">{stats.amlPending}</div>
        </div>
        {(stats.amlFailed ?? 0) > 0 && (
          <div className="card border-l-4 border-red-400">
            <div className="text-sm text-gray-600">AML Failed</div>
            <div className="text-3xl font-bold mt-2 text-red-500">{stats.amlFailed}</div>
          </div>
        )}
        <div className="card border-l-4 border-orange-500">
          <div className="text-sm text-gray-600">Manual Review</div>
          <div className="text-3xl font-bold mt-2 text-orange-600">{stats.manualReview}</div>
        </div>
        <div className="card border-l-4 border-green-500">
          <div className="text-sm text-gray-600">Completed</div>
          <div className="text-3xl font-bold mt-2 text-green-600">{stats.completed}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              className="select-field w-full"
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            >
              {FILTER_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Profile Type</label>
            <select
              className="select-field w-full"
              value={filters.profileType}
              onChange={(e) => setFilters(prev => ({ ...prev, profileType: e.target.value }))}
            >
              <option value="ALL">All Types</option>
              <option value="MEMBER">New Members</option>
              <option value="EXISTING_MEMBER">Existing Members</option>
              <option value="VISITOR">Visitors</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                className="input-field w-full pl-10"
                placeholder="Search by name or profile ID..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              />
            </div>
          </div>
        </div>
      </div>

      {/* DVS pipeline + OCR captures (tabbed) */}
      <div className="card">
        <div className="border-b border-gray-200 mb-4">
          <nav className="flex gap-1 -mb-px" aria-label="Verification queue lists">
            <button
              type="button"
              onClick={() => setListTab('dvs')}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                listTab === 'dvs'
                  ? 'border-psg-teal text-psg-teal'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              <Shield className="w-4 h-4" />
              DVS / AML pipeline
              <span className="text-xs font-normal text-gray-500">({stats.total})</span>
            </button>
            <button
              type="button"
              onClick={() => setListTab('ocr')}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                listTab === 'ocr'
                  ? 'border-psg-teal text-psg-teal'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              <ScanLine className="w-4 h-4" />
              OCR captures
              <span className="text-xs font-normal text-gray-500">({stats.ocrCaptures ?? 0})</span>
            </button>
          </nav>
        </div>

        {listTab === 'dvs' ? (
          <>
            <p className="text-sm text-gray-600 mb-4">
              Rows enter when <strong>Submit KYC (DVS)</strong> runs—after OCR confirmation—not from Scan ID alone.
            </p>
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                {error}
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Profile</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">ProfileSafe ID</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Type</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">DVS</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 max-w-[200px]">DVS response</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">AML</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Documents</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Time</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Assigned</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {items.map(item => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium">{item.firstName} {item.lastName}</div>
                        <div className="text-xs text-gray-500 font-mono">{item.masterProfileId}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className="text-xs font-mono text-gray-600"
                          title={resolveProfileSafeId(item) || 'Not captured — re-scan or check OCR data'}
                        >
                          {formatProfileSafeIdShort(resolveProfileSafeId(item))}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {getProfileTypeBadge(item.profileType)}
                        {item.visitorSource && (
                          <div className="text-xs text-gray-500 mt-1">
                            Source: {item.visitorSource}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(item.onboardingStatus)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded ${
                          item.dvsStatus === 'VERIFIED' ? 'bg-green-100 text-green-800' :
                          item.dvsStatus === 'FAILED' ? 'bg-red-100 text-red-800' :
                          item.dvsStatus && String(item.dvsStatus).includes('PROCESSING') ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {(item.dvsStatus || 'PENDING').replace('DVS_', '')}
                        </span>
                      </td>
                      <td className="px-4 py-3 max-w-[220px]">
                        <p className="text-xs text-gray-600 line-clamp-2" title={dvsResponseSummary(item)}>
                          {dvsResponseSummary(item)}
                        </p>
                        {rowActionMessage?.id === item.id && (
                          <p className={`text-xs mt-1 ${rowActionMessage.type === 'ok' ? 'text-green-700' : 'text-red-700'}`}>
                            {rowActionMessage.text}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded ${
                          item.amlStatus === 'CLEAR' ? 'bg-green-100 text-green-800' :
                          item.amlStatus && String(item.amlStatus).includes('PROCESSING') ? 'bg-purple-100 text-purple-800' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {(item.amlStatus || 'PENDING').replace('AML_', '')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-gray-400" />
                          <span>{item.documentsCount}</span>
                          {item.hasFaceprint && (
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                              FP
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-gray-600">
                          <Clock className="w-3 h-3" />
                          <span className="text-xs">{getTimeSince(item.updatedAt)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {item.assignedTo ? (
                          <div className="flex items-center gap-1">
                            <User className="w-3 h-3 text-gray-400" />
                            <span className="text-xs">{item.assignedTo}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">Unassigned</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex flex-col items-end gap-1">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleViewProfile(item)}
                              className="btn-secondary text-xs"
                            >
                              <Eye className="w-3 h-3 inline mr-1" />
                              View
                            </button>
                            {canManage && (
                              <button
                                type="button"
                                onClick={() => runDvsForItem(item)}
                                disabled={retryingId === item.id}
                                className="btn-primary text-xs"
                                title="Run DVS using stored OCR fields; add card number (back) in detail view if required for NSW/QLD/SA"
                              >
                                {retryingId === item.id ? (
                                  <RefreshCcw className="w-3 h-3 inline mr-1 animate-spin" />
                                ) : (
                                  <Play className="w-3 h-3 inline mr-1" />
                                )}
                                Run DVS
                              </button>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {items.length === 0 && !loading && !error && (
                <div className="text-center py-12 text-gray-500">
                  <Shield className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No profiles in queue</p>
                  <p className="text-sm mt-1 max-w-lg mx-auto">
                    Rows appear after someone completes <strong>Submit KYC (DVS)</strong> from Profile
                    Onboarding (not after OCR alone or final profile submit). If you expected activity, confirm KYC was
                    submitted successfully.
                  </p>
                </div>
              )}

              {loading && (
                <div className="text-center py-12">
                  <RefreshCcw className="w-8 h-8 mx-auto mb-3 text-gray-400 animate-spin" />
                  <p className="text-gray-500">Loading verification queue...</p>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-600 mb-4">
              Each successful <strong>Scan ID</strong> (data capture) is logged here. Images are not stored—parsed fields only.
              DVS/AML appears in the pipeline tab after <strong>Submit KYC</strong>.
            </p>
            {!ocrListIncluded && (
              <p className="text-sm text-amber-900 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mb-4">
                OCR capture rows are hidden while you filter by pipeline status or profile type. Set Status and Profile Type to{' '}
                <strong>All</strong> to load recent OCR submissions (search still applies).
              </p>
            )}
            {ocrListIncluded && (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium text-gray-600">Profile</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-600">Document</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-600">ProfileSafe ID</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-600">Response</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-600">Time</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-600">Source</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {ocrItems.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="font-medium">
                              {item.firstName} {item.lastName}
                            </div>
                            <div className="mt-1">{getStatusBadge(item.onboardingStatus)}</div>
                          </td>
                          <td className="px-4 py-3 text-xs font-mono">
                            {item.patronSnapshot?.documentNumber || item.ocrData?.documentNumber || '—'}
                          </td>
                          <td className="px-4 py-3 text-xs font-mono text-gray-600 break-all">
                            {item.masterProfileId}
                          </td>
                          <td className="px-4 py-3 text-xs">{item.profileSafeResponseCode ?? '—'}</td>
                          <td className="px-4 py-3 text-xs text-gray-600">{getTimeSince(item.createdAt)}</td>
                          <td className="px-4 py-3 text-xs text-gray-500">{item.createdBy || '—'}</td>
                          <td className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() => handleViewProfile(item)}
                              className="btn-secondary text-xs"
                            >
                              <Eye className="w-3 h-3 inline mr-1" />
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {ocrItems.length === 0 && !loading && !error && (
                  <div className="text-center py-10 text-gray-500 text-sm">
                    <ScanLine className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                    <p>No OCR captures recorded yet.</p>
                    <p className="mt-1 max-w-md mx-auto">
                      Run <strong>Scan ID</strong> on Profile Onboarding (Documents step). Entries appear here as soon as ProfileSafe returns parsed fields.
                    </p>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Auto-refresh indicator */}
      <div className="text-center text-xs text-gray-500">
        Auto-refreshes every 30 seconds
      </div>

      {/* Profile Status Detail Modal */}
      {selectedProfile && (
        <div 
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" 
          onClick={() => setSelectedProfile(null)}
        >
          <div 
            className="w-full max-w-4xl bg-white rounded-lg shadow-xl max-h-[90vh] overflow-y-auto" 
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h3 className="text-xl font-semibold text-psg-teal">
                  {selectedProfile.firstName} {selectedProfile.lastName}
                </h3>
                <p className="text-sm text-gray-500 font-mono mt-1">
                  {selectedProfile.masterProfileId}
                </p>
                {resolveProfileSafeId(selectedProfile) && (
                  <p className="text-xs text-gray-500 font-mono mt-0.5" title="ProfileSafe profileID (GreenID ref key)">
                    ProfileSafe: {resolveProfileSafeId(selectedProfile)}
                  </p>
                )}
                {selectedProfile.greenidVerificationId && (
                  <p className="text-xs text-gray-500 font-mono mt-0.5" title="GreenID verification REF NO">
                    GreenID: {selectedProfile.greenidVerificationId}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {canManage && selectedProfile.rowKind !== 'OCR' && (
                  <>
                    <button
                      type="button"
                      onClick={() => runDvsForItem(selectedProfile)}
                      disabled={retryingId === selectedProfile.id}
                      className="btn-primary"
                    >
                      {retryingId === selectedProfile.id ? (
                        <RefreshCcw className="w-4 h-4 inline mr-2 animate-spin" />
                      ) : (
                        <Play className="w-4 h-4 inline mr-2" />
                      )}
                      Run DVS
                    </button>
                    {canRunAmlForItem(selectedProfile) && (
                      <button
                        type="button"
                        onClick={() => runAmlForItem(selectedProfile)}
                        disabled={runningAmlId === selectedProfile.id}
                        className="btn-secondary border-purple-300 text-purple-800"
                        title="GreenID watchlist PEP/sanctions (REST + SOAP detail)"
                      >
                        {runningAmlId === selectedProfile.id ? (
                          <RefreshCcw className="w-4 h-4 inline mr-2 animate-spin" />
                        ) : (
                          <Shield className="w-4 h-4 inline mr-2" />
                        )}
                        Run AML
                      </button>
                    )}
                  </>
                )}
                <button
                  onClick={() => setSelectedProfile(null)}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {selectedProfile.rowKind === 'OCR' ? (
                <>
                  <div className="bg-teal-50 border border-teal-100 rounded-lg p-4 text-sm text-teal-900">
                    ProfileSafe OCR capture (pre-DVS). Parsed fields only; ID images and selfie are not stored on the server.
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Response code:</span>{' '}
                      <span className="font-mono">{selectedProfile.profileSafeResponseCode ?? '—'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Recorded:</span>{' '}
                      {selectedProfile.createdAt ? new Date(selectedProfile.createdAt).toLocaleString() : '—'}
                    </div>
                    <div>
                      <span className="text-gray-600">By:</span> {selectedProfile.createdBy ?? '—'}
                    </div>
                    <div>
                      <span className="text-gray-600">Tenant / group:</span> {selectedProfile.groupId ?? '—'}
                    </div>
                  </div>
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Parsed patron snapshot</h4>
                    <pre className="text-xs bg-gray-50 p-3 rounded overflow-auto max-h-[420px] whitespace-pre-wrap">
                      {JSON.stringify(selectedProfile.patronSnapshot || selectedProfile.ocrData || {}, null, 2)}
                    </pre>
                  </div>
                  <p className="text-sm text-gray-600">
                    When the operator confirms OCR and runs <strong>Submit KYC</strong>, a pipeline row is created for DVS/AML.
                  </p>
                </>
              ) : (
              <>
              {/* Overall Status */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Overall Status</h4>
                <div className="flex items-center gap-4">
                  {getStatusBadge(selectedProfile.onboardingStatus)}
                  <div className="flex-1">
                    <div className="text-sm text-gray-600">
                      Created: {new Date(selectedProfile.createdAt).toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-600">
                      Last Updated: {new Date(selectedProfile.updatedAt).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Profile Information */}
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Profile Information</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Profile Type:</span>{' '}
                    <span className="font-medium">
                      {getProfileTypeBadge(selectedProfile.profileType)}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Date of Birth:</span>{' '}
                    <span className="font-medium">{formatDobDisplay(selectedProfile.dateOfBirth)}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">ProfileSafe ID:</span>{' '}
                    <span className="font-mono text-xs break-all">
                      {resolveProfileSafeId(selectedProfile) || '—'}
                    </span>
                  </div>
                  {selectedProfile.greenidVerificationId && (
                    <div className="col-span-2">
                      <span className="text-gray-600">GreenID verification:</span>{' '}
                      <span className="font-mono text-xs break-all">{selectedProfile.greenidVerificationId}</span>
                    </div>
                  )}
                  {selectedProfile.visitorSource && (
                    <div>
                      <span className="text-gray-600">Visitor Source:</span>{' '}
                      <span className="font-medium">{selectedProfile.visitorSource}</span>
                    </div>
                  )}
                  {selectedProfile.assignedTo && (
                    <div>
                      <span className="text-gray-600">Assigned To:</span>{' '}
                      <span className="font-medium">{selectedProfile.assignedTo}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Verification Progress */}
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-4">Verification Progress</h4>
                
                {/* Documents */}
                <div className="mb-4 pb-4 border-b">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-gray-400" />
                      <span className="font-medium">Documents</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">
                        {selectedProfile.documentsCount} uploaded
                      </span>
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-psg-teal h-2 rounded-full transition-all" 
                      style={{ width: '100%' }}
                    />
                  </div>
                  <QueueDocumentGallery profile={selectedProfile} />
                  {selectedProfile.payoutId && (
                    <p className="text-xs text-gray-500 mt-2">
                      Linked payout: <span className="font-mono">{selectedProfile.payoutId}</span>
                      {selectedProfile.kycRegistryId ? (
                        <>
                          {' '}
                          · KYC registry #{selectedProfile.kycRegistryId}
                        </>
                      ) : null}
                    </p>
                  )}
                </div>

                {/* DVS Verification */}
                <div className="mb-4 pb-4 border-b">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-gray-400" />
                      <span className="font-medium">Document Verification (DVS)</span>
                    </div>
                    <div>
                      <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                        selectedProfile.dvsStatus === 'VERIFIED' 
                          ? 'bg-green-100 text-green-800' 
                          : selectedProfile.dvsStatus && String(selectedProfile.dvsStatus).includes('PROCESSING')
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {(selectedProfile.dvsStatus || 'PENDING').replace('DVS_', '')}
                      </span>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all ${
                        selectedProfile.dvsStatus === 'VERIFIED' 
                          ? 'bg-green-500' 
                          : selectedProfile.dvsStatus && String(selectedProfile.dvsStatus).includes('PROCESSING')
                          ? 'bg-blue-500'
                          : 'bg-yellow-500'
                      }`}
                      style={{ 
                        width: selectedProfile.dvsStatus === 'VERIFIED' 
                          ? '100%' 
                          : selectedProfile.dvsStatus && String(selectedProfile.dvsStatus).includes('PROCESSING')
                          ? '50%'
                          : '20%'
                      }}
                    />
                  </div>
                  {selectedProfile.dvsStatus && String(selectedProfile.dvsStatus).includes('PROCESSING') && (
                    <div className="mt-2 text-xs text-blue-600 flex items-center gap-1">
                      <RefreshCcw className="w-3 h-3 animate-spin" />
                      Verification in progress with external provider...
                    </div>
                  )}
                  {selectedProfile.dvsStatus === 'PENDING' && (
                    <div className="mt-2 text-xs text-yellow-600">
                      Waiting for document verification
                    </div>
                  )}
                  {selectedProfile.dvsProvider && (
                    <div className="mt-2 text-xs text-gray-600">
                      Document verification:{' '}
                      <span className="font-medium">{formatDvsProviderForUi(selectedProfile.dvsProvider)}</span>
                    </div>
                  )}
                  {isPayoutPipelineRow(selectedProfile) && (
                    <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-950">
                      This row was created from a <strong>payout</strong> (ID photos uploaded on mobile).
                      The payout API does not yet send structured licence fields — only images. Fields below are
                      what DVS uses (from OCR on save, or enter manually). For a full flow use{' '}
                      <strong>Profile Onboarding → Submit KYC (DVS)</strong>.
                    </div>
                  )}
                  <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3 text-sm">
                    <div className="font-medium text-gray-900 mb-2">DVS document data</div>
                    <p className="text-xs text-gray-600 mb-3">
                      Saved to this row when you Run DVS. Empty fields cause “missing OCR” errors.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <label className="block text-xs">
                        <span className="text-gray-600">Document type</span>
                        <select
                          className="input-field w-full mt-1"
                          value={dvsFields.document_type}
                          onChange={(e) => setDvsFields((f) => ({ ...f, document_type: e.target.value }))}
                        >
                          <option value="drivers_licence">Driver licence</option>
                          <option value="passport">Passport</option>
                          <option value="medicare">Medicare</option>
                        </select>
                      </label>
                      <label className="block text-xs">
                        <span className="text-gray-600">State</span>
                        <input
                          className="input-field w-full mt-1"
                          value={dvsFields.state_of_issue}
                          onChange={(e) => setDvsFields((f) => ({ ...f, state_of_issue: e.target.value }))}
                        />
                      </label>
                      <label className="block text-xs sm:col-span-2">
                        <span className="text-gray-600">Licence number</span>
                        <input
                          className="input-field w-full mt-1 font-mono"
                          value={dvsFields.document_number}
                          onChange={(e) => setDvsFields((f) => ({ ...f, document_number: e.target.value }))}
                        />
                      </label>
                      <label className="block text-xs sm:col-span-2">
                        <span className="text-gray-600">Card number * (GBG / AU DVS)</span>
                        <input
                          className="input-field w-full mt-1 font-mono"
                          value={dvsFields.document_card_number}
                          onChange={(e) => {
                            setDvsFields((f) => ({ ...f, document_card_number: e.target.value }))
                            setDvsCardNumber(e.target.value)
                          }}
                          placeholder="Separate from licence no. GBG test: 1111111111"
                          title="Required by GBG greenID for Australian driver licence DVS"
                        />
                      </label>
                      <label className="block text-xs">
                        <span className="text-gray-600">Given name</span>
                        <input
                          className="input-field w-full mt-1"
                          value={dvsFields.given_name}
                          onChange={(e) => setDvsFields((f) => ({ ...f, given_name: e.target.value }))}
                        />
                      </label>
                      <label className="block text-xs">
                        <span className="text-gray-600">Family name</span>
                        <input
                          className="input-field w-full mt-1"
                          value={dvsFields.family_name}
                          onChange={(e) => setDvsFields((f) => ({ ...f, family_name: e.target.value }))}
                        />
                      </label>
                      <label className="block text-xs">
                        <span className="text-gray-600">Date of birth (YYYY-MM-DD)</span>
                        <input
                          className="input-field w-full mt-1"
                          value={dvsFields.date_of_birth}
                          onChange={(e) => setDvsFields((f) => ({ ...f, date_of_birth: e.target.value }))}
                        />
                      </label>
                    </div>
                    {effectiveDvsFields(selectedProfile).ocr_source && (
                      <p className="text-xs text-teal-700 mt-2">
                        OCR source: {effectiveDvsFields(selectedProfile).ocr_source}
                      </p>
                    )}
                  </div>
                  <DvsResponsePanel item={selectedProfile} />
                </div>

                {/* AML/PEP Screening */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-gray-400" />
                      <span className="font-medium">AML/PEP Screening</span>
                    </div>
                    <div>
                      <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                        selectedProfile.amlStatus === 'CLEAR' 
                          ? 'bg-green-100 text-green-800' 
                          : selectedProfile.amlStatus && String(selectedProfile.amlStatus).includes('PROCESSING')
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {(selectedProfile.amlStatus || 'PENDING').replace('AML_', '')}
                      </span>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all ${
                        selectedProfile.amlStatus === 'CLEAR' 
                          ? 'bg-green-500' 
                          : selectedProfile.amlStatus && String(selectedProfile.amlStatus).includes('PROCESSING')
                          ? 'bg-purple-500'
                          : 'bg-yellow-500'
                      }`}
                      style={{ 
                        width: selectedProfile.amlStatus === 'CLEAR' 
                          ? '100%' 
                          : selectedProfile.amlStatus && String(selectedProfile.amlStatus).includes('PROCESSING')
                          ? '50%'
                          : '20%'
                      }}
                    />
                  </div>
                  {selectedProfile.amlStatus?.includes('PROCESSING') && (
                    <div className="mt-2 text-xs text-purple-600 flex items-center gap-1">
                      <RefreshCcw className="w-3 h-3 animate-spin" />
                      Screening against sanctions lists and PEP databases...
                    </div>
                  )}
                  {selectedProfile.amlStatus === 'PENDING' && (
                    <div className="mt-2 text-xs text-yellow-600">
                      {String(selectedProfile.dvsStatus || '').toUpperCase() === 'VERIFIED'
                        ? 'PEP/sanctions not run yet, or this row predates auto-AML. Use Run AML (GreenID watchlist REST + SOAP).'
                        : 'Waiting for DVS verification before AML can run'}
                    </div>
                  )}
                  {selectedProfile.amlStatus === 'PENDING_REVIEW' &&
                    String(selectedProfile.onboardingStatus || '').toUpperCase() === 'COMPLETED' && (
                    <div className="mt-2 text-xs text-teal-700">
                      Screening completed with no PEP/sanctions hits; status label is stale. Click Run AML once to refresh to CLEAR.
                    </div>
                  )}
                </div>

                {/* Biometric */}
                {selectedProfile.hasFaceprint && (
                  <div className="pt-4 border-t">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <User className="w-5 h-5 text-gray-400" />
                        <span className="font-medium">Biometric (FacePrint)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-green-600">Captured</span>
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Manual Review Alert */}
              {selectedProfile.onboardingStatus === 'MANUAL_REVIEW' && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-orange-900 mb-1">
                        Manual Review Required
                      </h4>
                      <p className="text-sm text-orange-800">
                        This profile requires manual review by compliance staff. Possible reasons:
                      </p>
                      <ul className="text-sm text-orange-800 list-disc list-inside mt-2 space-y-1">
                        <li>AML/PEP screening found potential matches</li>
                        <li>Document verification failed multiple attempts</li>
                        <li>Conflicting information detected</li>
                        <li>Staff manually flagged for review</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t">
                <button
                  onClick={() => window.location.href = `/profile-safe/search?masterProfileId=${encodeURIComponent(selectedProfile.masterProfileId || '')}`}
                  className="btn-secondary"
                >
                  View Full Profile
                </button>
                <div className="flex items-center gap-2">
                  {selectedProfile.onboardingStatus === 'MANUAL_REVIEW' && (
                    <>
                      <button className="btn-secondary text-red-600 hover:bg-red-50">
                        Reject
                      </button>
                      <button className="btn-primary">
                        Approve
                      </button>
                    </>
                  )}
                </div>
              </div>
              </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
